import React from "react";
import { Document, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

const colors = {
  navy: "#0b1833",
  panel: "#142240",
  cyan: "#24c4ef",
  blue: "#edf5fb",
  muted: "#6883a3",
  white: "#ffffff",
  border: "#d7e3f0",
  darkBorder: "#2d4569",
};

const styles = StyleSheet.create({
  page: { padding: 36, fontSize: 9, color: colors.navy, fontFamily: "Helvetica", backgroundColor: "#ffffff" },
  header: { backgroundColor: colors.navy, borderRadius: 14, padding: 20, marginBottom: 13, minHeight: 120 },
  headerContent: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  headerText: { flexGrow: 1, maxWidth: 405, marginRight: 14 },
  eyebrow: { color: colors.cyan, fontSize: 8, fontFamily: "Helvetica-Bold", letterSpacing: 1.2, marginBottom: 8 },
  title: { color: colors.white, fontSize: 22, fontFamily: "Helvetica-Bold", marginBottom: 7 },
  subtitle: { color: "#d1e0ee", fontSize: 10, lineHeight: 1.35 },
  organizationName: { color: "#9ab3cc", fontSize: 9, marginTop: 4 },
  logoBox: { width: 76, height: 76, backgroundColor: colors.white, borderRadius: 10, padding: 7, alignItems: "center", justifyContent: "center" },
  logo: { width: 62, height: 62, objectFit: "contain" },
  logoFallback: { color: colors.navy, fontFamily: "Helvetica-Bold", fontSize: 11, textAlign: "center" },
  generated: { alignSelf: "flex-start", paddingVertical: 6, paddingHorizontal: 10, borderRadius: 10, backgroundColor: colors.blue, color: colors.muted, fontSize: 8, marginBottom: 11 },
  sectionHeading: { flexDirection: "row", alignItems: "center", marginTop: 11, marginBottom: 8 },
  sectionAccent: { width: 4, height: 16, borderRadius: 3, backgroundColor: colors.cyan, marginRight: 7 },
  sectionTitle: { color: colors.navy, fontSize: 14, fontFamily: "Helvetica-Bold" },
  kpiGrid: { flexDirection: "row", flexWrap: "wrap", marginHorizontal: -3 },
  kpi: { width: "33.33%", paddingHorizontal: 3, marginBottom: 6 },
  kpiInner: { minHeight: 57, backgroundColor: colors.panel, borderRadius: 9, padding: 10, borderLeftWidth: 3, borderLeftColor: colors.cyan },
  kpiLabel: { color: "#adc1d7", fontSize: 8, marginBottom: 5 },
  kpiValue: { color: colors.white, fontSize: 15, fontFamily: "Helvetica-Bold" },
  table: { borderWidth: 1, borderColor: colors.darkBorder, borderRadius: 8, overflow: "hidden" },
  row: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: colors.border, minHeight: 29, alignItems: "center", backgroundColor: colors.white },
  headRow: { backgroundColor: colors.navy, borderBottomWidth: 0, minHeight: 31 },
  headText: { color: colors.white, fontFamily: "Helvetica-Bold", fontSize: 8 },
  cell: { paddingVertical: 7, paddingHorizontal: 7, fontSize: 8, color: colors.navy, lineHeight: 1.3 },
  muted: { color: colors.muted },
  ticket: { width: "28%" },
  number: { width: "12%", textAlign: "right" },
  price: { width: "18%", textAlign: "right" },
  options: { width: "42%" },
  promoName: { width: "25%" },
  promoUsage: { width: "14%" },
  promoDiscount: { width: "33%" },
  promoReservation: { width: "28%" },
  optionLine: { marginBottom: 3, lineHeight: 1.3 },
  footer: { position: "absolute", bottom: 16, left: 36, right: 36, paddingTop: 6, borderTopWidth: 1, borderTopColor: colors.border, textAlign: "center", color: colors.muted, fontSize: 7 },
  empty: { padding: 14, textAlign: "center", color: colors.muted },
});

const formatCurrency = (value, locale) =>
  new Intl.NumberFormat(locale || "fr-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  })
    .format(Number(value || 0))
    .replace(/[\u00a0\u202f]/g, " ");

const valueLabel = (option, locale) =>
  `${option.label || "-"} x ${Number(option.amountSold || 0)}${
    Number(option.priceDelta || 0)
      ? ` (${Number(option.priceDelta || 0) >= 0 ? "+" : ""}${formatCurrency(option.priceDelta, locale)})`
      : ""
  }`;

function SectionTitle({ children }) {
  return (
    <View style={styles.sectionHeading}>
      <View style={styles.sectionAccent} />
      <Text style={styles.sectionTitle}>{children}</Text>
    </View>
  );
}

export default function TicketingSalesReportPDF({ report, labels }) {
  const promoCodes = Array.isArray(report.promoCodes) ? report.promoCodes : [];
  const organisationInitials = String(report.organizationName || "C")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerText}>
              <Text style={styles.eyebrow}>COMITTY</Text>
              <Text style={styles.title}>{labels.title}</Text>
              <Text style={styles.subtitle}>{report.eventName}</Text>
              <Text style={styles.organizationName}>{report.organizationName}</Text>
            </View>
            <View style={styles.logoBox}>
              {report.organizationLogo ? (
                <Image src={report.organizationLogo} style={styles.logo} />
              ) : (
                <Text style={styles.logoFallback}>{organisationInitials}</Text>
              )}
            </View>
          </View>
        </View>
        <Text style={styles.generated}>{`${labels.generatedAt}: ${report.generatedAt}`}</Text>

        <SectionTitle>{labels.summary}</SectionTitle>
        <View style={styles.kpiGrid}>
          {[
            [labels.theoreticalRevenue, formatCurrency(report.theoreticalRevenue, report.locale)],
            [labels.collectedMoney, formatCurrency(report.collectedMoney, report.locale)],
            [labels.participants, report.participantsCount],
            [labels.paidCheckoutTickets, report.paidCheckoutTickets],
            [labels.ticketsDistributed, report.ticketsDistributed],
            [labels.ticketsScanned, report.ticketsScanned],
            [labels.optionsSold, report.optionsSold],
          ].map(([label, value]) => (
            <View style={styles.kpi} key={label}>
              <View style={styles.kpiInner}>
                <Text style={styles.kpiLabel}>{label}</Text>
                <Text style={styles.kpiValue}>{value}</Text>
              </View>
            </View>
          ))}
        </View>

        <SectionTitle>{labels.ticketDetails}</SectionTitle>
        <View style={styles.table}>
          <View style={[styles.row, styles.headRow]} fixed>
            <Text style={[styles.cell, styles.headText, styles.ticket]}>{labels.ticket}</Text>
            <Text style={[styles.cell, styles.headText, styles.number]}>{labels.sold}</Text>
            <Text style={[styles.cell, styles.headText, styles.price]}>{labels.revenue}</Text>
            <Text style={[styles.cell, styles.headText, styles.options]}>{labels.options}</Text>
          </View>
          {report.tickets.map((ticket) => (
            <View style={styles.row} key={ticket.id} wrap={false}>
              <Text style={[styles.cell, styles.ticket]}>{ticket.name}</Text>
              <Text style={[styles.cell, styles.number]}>{ticket.amountSold}</Text>
              <Text style={[styles.cell, styles.price]}>{formatCurrency(ticket.revenue, report.locale)}</Text>
              <View style={[styles.cell, styles.options]}>
                {ticket.options.length ? ticket.options.map((option) => (
                  <Text key={option.id} style={styles.optionLine}>{`${option.groupName}: ${valueLabel(option, report.locale)}`}</Text>
                )) : <Text style={styles.muted}>{labels.noOptions}</Text>}
              </View>
            </View>
          ))}
        </View>

        <SectionTitle>{labels.promoDetails}</SectionTitle>
        <View style={styles.table}>
          <View style={[styles.row, styles.headRow]} fixed>
            <Text style={[styles.cell, styles.headText, styles.promoName]}>{labels.promo}</Text>
            <Text style={[styles.cell, styles.headText, styles.promoUsage]}>{labels.usage}</Text>
            <Text style={[styles.cell, styles.headText, styles.promoDiscount]}>{labels.discounts}</Text>
            <Text style={[styles.cell, styles.headText, styles.promoReservation]}>{labels.reservation}</Text>
          </View>
          {promoCodes.length ? promoCodes.map((promo) => (
            <View style={styles.row} key={promo.id} wrap={false}>
              <Text style={[styles.cell, styles.promoName]}>{`${promo.name || "-"} (${promo.code || "-"})\n${promo.isActive ? labels.active : labels.inactive}${promo.isPartnerCode ? ` - ${labels.partner}` : ""}${promo.excludeFromTheoreticalRevenue ? `\n${labels.excludeFromTheoreticalRevenue}` : ""}`}</Text>
              <Text style={[styles.cell, styles.promoUsage]}>{promo.maxUsage === 0 ? `${promo.currentUsage || 0} / ${labels.unlimited}` : `${promo.currentUsage || 0} / ${promo.maxUsage || 0}`}</Text>
              <Text style={[styles.cell, styles.promoDiscount]}>{promo.discountsText || labels.none}</Text>
              <Text style={[styles.cell, styles.promoReservation]}>{promo.reservationText || labels.none}</Text>
            </View>
          )) : <Text style={styles.empty}>{labels.noPromoCodes}</Text>}
        </View>
        <Text
          fixed
          style={styles.footer}
          render={({ pageNumber, totalPages }) => `${labels.footer} - ${pageNumber} / ${totalPages}`}
        />
      </Page>
    </Document>
  );
}
